import { FormEvent, useEffect, useRef, useState } from "react";
import {
  appendRestaurantOperatorReply,
  loadRestaurantCustomerConversation,
  resetRestaurantCustomerConversation,
  resumeRestaurantCustomerAi,
  RESTAURANT_CUSTOMER_CONVERSATION_EVENT,
  type RestaurantCustomerConversationState,
} from "./restaurant-customer-conversation";

export function RestaurantCustomerHandoffTray({ businessSlug, businessName }: { readonly businessSlug: string; readonly businessName: string }) {
  const [conversation, setConversation] = useState<RestaurantCustomerConversationState>(() => loadRestaurantCustomerConversation(businessSlug, businessName));
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const submitLockRef = useRef(false);

  useEffect(() => {
    const reload = (event?: Event) => {
      const slug = event instanceof CustomEvent && typeof event.detail?.slug === "string" ? event.detail.slug : businessSlug;
      if (slug === businessSlug) {
        const next = loadRestaurantCustomerConversation(businessSlug, businessName);
        setConversation(next);
        if (next.routingMode === "human") setOpen(true);
      }
    };
    window.addEventListener(RESTAURANT_CUSTOMER_CONVERSATION_EVENT, reload);
    window.addEventListener("focus", reload);
    return () => {
      window.removeEventListener(RESTAURANT_CUSTOMER_CONVERSATION_EVENT, reload);
      window.removeEventListener("focus", reload);
    };
  }, [businessSlug, businessName]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = reply.trim();
    if (!text || conversation.routingMode !== "human" || submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      const next = appendRestaurantOperatorReply({
        slug: businessSlug,
        businessName,
        text,
        id: `operator-${conversation.updatedAt}`,
      });
      setConversation(next);
      setReply("");
    } finally {
      submitLockRef.current = false;
    }
  }

  function resume() {
    setConversation(resumeRestaurantCustomerAi(businessSlug, businessName));
  }

  function clear() {
    setConversation(resetRestaurantCustomerConversation(businessSlug, businessName));
    setReply("");
  }

  const latest = conversation.messages.filter((message) => message.role !== "system").slice(-8);
  return <aside className={`rcx-handoff-tray${open ? " open" : ""}`} aria-label="Atención cliente demo">
    <button className="rcx-handoff-toggle" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
      <span>💬 Atención cliente</span><strong className={conversation.routingMode}>{conversation.routingMode === "human" ? "Requiere persona" : "IA activa"}</strong>
    </button>
    {open ? <div className="rcx-handoff-panel">
      <header><div><small>Webchat demo</small><strong>{businessName}</strong></div><button type="button" onClick={() => setOpen(false)} aria-label="Cerrar">×</button></header>
      <div className="rcx-handoff-status"><span className={conversation.routingMode}>{conversation.routingMode === "human" ? "Control humano" : "Automático"}</span><p>{conversation.routingMode === "human" ? conversation.handoffReason ?? "El cliente pidió ayuda humana." : "El Customer Agent puede responder con su allowlist pública."}</p></div>
      <div className="rcx-handoff-messages" aria-live="polite">{latest.map((message) => <div className={message.role} key={message.id}><small>{message.role === "customer" ? "Cliente" : message.role === "operator" ? "Equipo" : "Asistente"}</small><span>{message.text}</span></div>)}</div>
      {conversation.routingMode === "human" ? <form onSubmit={submit}><label className="rcx-sr-only" htmlFor="customer-operator-reply">Respuesta del equipo</label><textarea id="customer-operator-reply" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Responder como equipo…" rows={3} /><button type="submit" disabled={!reply.trim()}>Enviar respuesta humana</button><button type="button" className="secondary" onClick={resume}>Reanudar IA</button></form> : <p className="rcx-handoff-empty">No hay una conversación esperando intervención humana.</p>}
      <footer><button type="button" onClick={clear}>Limpiar conversación demo</button></footer>
    </div> : null}
  </aside>;
}
